import {
  TooltipSortController,
  TooltipSortItem,
  DataVisualElementController,
} from '@sas-dvr/ltjs-bird';
import { SortDirection, VisualizationManager } from '@sas-dvr/ltjs-commons';
import {
  ReportContextMenuItem,
  ReportContextMenuSection,
  ReportContextMenuState,
} from '@sas-dvr/internal-va-react-core/store/reportContextMenu/ReportContextMenuState';
import { filterNulls } from './arrayUtils';
import { AnyAction } from 'redux';
import { Observable, merge, EMPTY, of } from 'rxjs';
import { StateObservable } from 'redux-observable';
import { tap, switchMap, ignoreElements, map, distinctUntilChanged } from 'rxjs/operators';
import { withRetained, withRetainedArray, ofActionCreatorType } from './rxUtils';
import { SortActions } from '../actions/sortActions';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { ContextMenuItems } from './contextMenu';

export function toggleSortDirection(
  sortController: TooltipSortController,
  sortItem: TooltipSortItem,
  isPrimary: boolean
) {
  // Reverse the current sort direction. SortDirection.DATA defaults to ASCENDING
  const currentSort = sortItem.getSortDirection();
  const newSort =
    currentSort === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
  applySort(sortController, sortItem, newSort, isPrimary);
}

export function applySort(
  sortController: TooltipSortController,
  sortItem: TooltipSortItem,
  direction: SortDirection,
  isPrimary: boolean
) {
  sortItem.setSortDirection(direction);
  sortItem.setPrimary(isPrimary);
  sortController.sort(sortItem);
}

export function createSortMenuHandler(
  dataVisualElementController: DataVisualElementController,
  visualizationManager: VisualizationManager | null, // nullable to support prompt elements
  id: string,
  myActions$: Observable<AnyAction>,
  state$: StateObservable<ReportState>
): Observable<AnyAction> {
  return state$.pipe(
    map((state) => state.ui.reportContextMenu),
    distinctUntilChanged((a, b) => a.target === b.target && a.graphPosition === b.graphPosition),
    switchMap(({ target, graphPosition }) => {
      if (target?.id !== id || dataVisualElementController.isBusy()) {
        return EMPTY;
      }
      const pickResults =
        visualizationManager && graphPosition
          ? visualizationManager.getPickResults(
              graphPosition.localX,
              graphPosition.localY,
              false,
              false
            )
          : null;
      const sortController = dataVisualElementController.getTooltipSortControllerWithAllSortItems(
        pickResults
      );
      if (!sortController) {
        return EMPTY;
      }
      return loadSortController(sortController, id, myActions$);
    })
  );
}

function loadSortController(
  sortController: TooltipSortController,
  id: string,
  myActions$: Observable<AnyAction>
): Observable<AnyAction> {
  const sortItems = filterNulls(sortController.getSortItems());

  const handleContextMenu$ = merge(
    myActions$.pipe(
      ofActionCreatorType<typeof SortActions.actions.sort>(SortActions.types.Sort),
      tap((action) => {
        const { direction, isPrimary, index } = action.payload.data;
        applySort(sortController, sortItems[index], direction, isPrimary);
      })
    ),
    myActions$.pipe(
      ofActionCreatorType<typeof SortActions.actions.removeItem>(SortActions.types.RemoveItem),
      tap((action) => {
        const sortItem = sortItems[action.payload.index];
        sortController.removeSort(sortItem);
      })
    ),
    myActions$.pipe(
      ofActionCreatorType<typeof SortActions.actions.removeLevel>(SortActions.types.RemoveLevel),
      tap(() => {
        sortController.removeSortLevel();
      })
    )
  ).pipe(ignoreElements());

  const setMenuItemAction = ReportContextMenuState.actions.setObjectItem(
    ContextMenuItems.SORT,
    createSortMenuItem(id, sortController) ?? undefined
  );

  return merge(of(setMenuItemAction), handleContextMenu$).pipe(
    withRetained(sortController),
    withRetainedArray(sortItems)
  );
}

function createSortMenuItem(
  id: string,
  sortController: TooltipSortController
): ReportContextMenuItem | null {
  const sortItems = filterNulls(sortController.getSortItems());
  if (!sortItems.length) {
    return null;
  }
  const subSections =
    sortItems.length === 1
      ? getOptionsForSingleItem(id, sortController, sortItems[0])
      : getOptions(id, sortController, sortItems);

  return {
    subSections,
    text: { key: 'ContextMenu.sort.txt' },
  };
}

function getOptions(
  id: string,
  sortController: TooltipSortController,
  sortItems: TooltipSortItem[]
) {
  const sections = getAddRemoveOptions(id, sortItems);
  if (sortController.canRemoveSortLevel()) {
    sections.push({
      items: [
        {
          text: { key: 'ContextMenu.removeSortLevel.txt' },
          checked: false,
          action: SortActions.actions.removeLevel(id),
        },
      ],
    });
  }
  return sections;
}

function getOptionsForSingleItem(
  id: string,
  sortController: TooltipSortController,
  sortItem: TooltipSortItem
): ReportContextMenuSection[] {
  const sections = getAddRemoveOptions(id, [sortItem]);
  const sortItemLabel = sortItem.getLabel() || '';
  const currentSortDirection = sortItem.getSortDirection();
  const index = 0;
  // Add ascending and descending menu actions would be added only if
  // the current sort item is not sorted as primary.
  if (!sortItem.isPrimary()) {
    sections.push({
      items: [
        {
          text: { key: 'ContextMenu.sortAddAscending.fmt', args: { role: sortItemLabel } },
          checked: currentSortDirection === SortDirection.ASCENDING,
          action: SortActions.actions.sort(id, {
            direction: SortDirection.ASCENDING,
            isPrimary: false,
            index,
          }),
        },
        {
          text: { key: 'ContextMenu.sortAddDescending.fmt', args: { role: sortItemLabel } },
          checked: currentSortDirection === SortDirection.DESCENDING,
          action: SortActions.actions.sort(id, {
            direction: SortDirection.DESCENDING,
            isPrimary: false,
            index,
          }),
        },
      ],
    });
  }
  // We need to check with portable if we can remove the sort on the current column
  // since it has the information about all the sort items. When user right clicks on
  // a column header, we get only one sort item back from portable. So Viewer doesn't
  // know the sort state of other columns in the table.
  if (sortController.canRemoveSort(sortItem)) {
    sections.push({
      items: [
        {
          text: { key: 'ContextMenu.removeSortItem.fmt', args: { role: sortItemLabel } },
          checked: false,
          action: SortActions.actions.removeItem(id, index),
        },
      ],
    });
  }
  return sections;
}

function getAddRemoveOptions(id: string, sortItems: TooltipSortItem[]): ReportContextMenuSection[] {
  return sortItems.map((sortItem, index) => {
    const sortItemLabel = sortItem.getLabel() || '';
    const currentSortDirection = sortItem.getSortDirection();
    return {
      items: [
        {
          text: { key: 'ContextMenu.sortAscending.fmt', args: { role: sortItemLabel } },
          checked: currentSortDirection === SortDirection.ASCENDING,
          action: SortActions.actions.sort(id, {
            direction: SortDirection.ASCENDING,
            isPrimary: true,
            index,
          }),
        },
        {
          text: { key: 'ContextMenu.sortDescending.fmt', args: { role: sortItemLabel } },
          checked: currentSortDirection === SortDirection.DESCENDING,
          action: SortActions.actions.sort(id, {
            direction: SortDirection.DESCENDING,
            isPrimary: true,
            index,
          }),
        },
      ],
    };
  });
}
